import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import { supabase } from '../lib/supabase';

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong. Please try again.';

const SetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isInvite = pathname === '/set-password';
  const [checking, setChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    // getSession once; supabase may also fire PASSWORD_RECOVERY / SIGNED_IN a
    // tick later once it parses the invite URL hash. Subscribe so a valid link
    // that is still being exchanged does not flash "expired".
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session) setHasSession(true);
      setChecking(false);
    });
const { data: sub } = supabase.auth.onAuthStateChange((event: string, session: unknown | null) => {
      if (!active) return;
      const evt = event as string;
      if (evt === 'PASSWORD_RECOVERY' || evt === 'SIGNED_IN') {
        if (session) {
          setHasSession(true);
          setChecking(false);
        }
      } else if (evt === 'SIGNED_OUT') {
        setHasSession(false);
        setChecking(false);
      }
    }) as { data: { subscription: { unsubscribe: () => void } } };
    return () => {
      active = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      navigate('/', { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="main-content" style={{ display: 'flex', minHeight: '100vh', maxWidth: 460, flexDirection: 'column', justifyContent: 'center' }}>
      <div className="mb-8 text-center">
        <h1 className="text-accent" style={{ fontSize: '3rem', fontWeight: 900 }}>FITFLEX</h1>
      </div>
      <div className="card border border-fit-bg-tertiary bg-fit-bg-secondary p-8 md:p-10">
        {checking ? (
          <p className="text-center text-fit-text-secondary">Checking your secure link…</p>
        ) : !hasSession ? (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white">This link has expired</h1>
            <p className="mt-3 text-[13px] leading-relaxed text-fit-text-secondary">
              Invitation and password-reset links can only be used once. Request a fresh link to continue.
            </p>
            <Link to="/login" className="btn btn-primary mt-6 no-underline">Return to login</Link>
          </div>
        ) : (
          <>
            <div className="mb-7 text-center">
              <h1 className="text-3xl font-bold tracking-tight text-white">
                {isInvite ? 'Create your password' : 'Choose a new password'}
              </h1>
              <p className="mt-2 text-[13px] text-fit-text-secondary">
                {isInvite ? 'Your coach has invited you to Fitflex.' : 'Secure your Fitflex account with a new password.'}
              </p>
            </div>
            {error && <div className="mb-4 rounded-fit-md border border-fit-line-strong bg-white/5 px-4 py-3 text-[13px] text-white">{error}</div>}
            <form onSubmit={save} className="flex flex-col gap-4">
              <Input
                type="password"
                placeholder="New password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
              <Input
                type="password"
                placeholder="Confirm password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required
              />
              <Button type="submit" fullWidth isLoading={saving}>Save password</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SetPassword;
